import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, BookHeart, Lock } from 'lucide-react';

const STORAGE_KEY = 'gw_reflection_journal';

const thoughtExamples = [
  'It’s too much time or energy to move.',
  'I have no time to move.',
  'I can’t exercise like I want to, so I won’t.',
  'The only thing that picks me up is sugar.',
  'I need a quick fix to fuel me quickly so I can get back to life.',
  'It’s too hard to think about my food.',
];

export const ReflectionJournal = () => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState({ emotions: '', thoughts: '', notes: '' });
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setEntries({ emotions: '', thoughts: '', notes: '', ...JSON.parse(raw) });
    } catch (e) { /* ignore */ }
  }, []);

  const update = (field, value) => {
    const next = { ...entries, [field]: value };
    setEntries(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setSaved(true);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 1500);
    } catch (e) { /* ignore */ }
  };

  return (
    <section className="max-w-6xl mx-auto px-6 mt-12" data-testid="reflection-journal-section">
      <div className="bg-white rounded-3xl shadow-card border border-stone-100 overflow-hidden">
        <button
          onClick={() => setOpen(!open)}
          className="w-full p-6 md:p-7 flex items-center justify-between gap-4 text-left hover:bg-stone-50 transition-colors"
          data-testid="reflection-journal-toggle"
        >
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <BookHeart size={20} className="text-green-700" />
            </div>
            <div className="min-w-0">
              <p className="text-terracotta font-body text-xs tracking-widest uppercase mb-1">Gentle & Private</p>
              <h2 className="font-heading text-2xl md:text-3xl text-forest">Reflection &amp; Journal</h2>
              <p className="text-sm font-body text-stone-500 mt-1">A soft space to explore your emotions, thoughts &amp; notes</p>
            </div>
          </div>
          <ChevronDown
            size={22}
            className={`text-forest flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="px-6 md:px-10 pb-8 md:pb-10 space-y-8">
                <div className="flex items-start gap-2 text-xs font-body text-stone-500 bg-stone-50 rounded-xl p-3 border border-stone-100">
                  <Lock size={14} className="text-stone-400 flex-shrink-0 mt-0.5" />
                  <span>Everything you write here stays on your own device. It is private to you and never shared.</span>
                </div>

                {/* Emotions */}
                <div data-testid="reflection-emotions-block">
                  <h3 className="font-heading text-xl text-forest mb-2">Emotions</h3>
                  <p className="text-stone-700 font-body text-sm leading-relaxed mb-3">
                    We want to explore emotions with a very gentle approach. Many of us are harsh with ourselves or
                    disconnected from our emotions. Maybe lean in and ask&hellip;
                  </p>
                  <ul className="text-stone-600 font-body text-sm leading-relaxed space-y-1.5 mb-4 pl-4 border-l-2 border-green-200">
                    <li>What emotions did I feel today?</li>
                    <li>Where did I feel them?</li>
                    <li>When I felt <em>“x”</em>, where did I experience it in my body? What did it feel like <em>in</em> my body?</li>
                    <li>Did it have a colour? A texture?</li>
                    <li>Did it feel open, closed, or something else?</li>
                    <li>Did it feel safe, scary, or something else?</li>
                  </ul>
                  <textarea
                    value={entries.emotions}
                    onChange={(e) => update('emotions', e.target.value)}
                    placeholder="Lean in gently and write whatever arises…"
                    rows={5}
                    className="w-full rounded-2xl border border-stone-200 bg-stone-50 focus:bg-white focus:border-green-400 focus:ring-1 focus:ring-green-300 outline-none p-4 font-body text-sm text-stone-700 resize-y transition-colors"
                    data-testid="reflection-emotions-input"
                  />
                </div>

                {/* Thought Patterns */}
                <div data-testid="reflection-thoughts-block">
                  <h3 className="font-heading text-xl text-forest mb-2">Thought Patterns</h3>
                  <p className="text-stone-700 font-body text-sm leading-relaxed mb-3">
                    What were my thoughts today? What was my thought when <em>“x”</em> happened? A few patterns that often
                    show up:
                  </p>
                  <ul className="text-stone-600 font-body text-sm leading-relaxed space-y-1.5 mb-4 pl-4 border-l-2 border-green-200">
                    {thoughtExamples.map((t, i) => (
                      <li key={i}><em>“{t}”</em></li>
                    ))}
                  </ul>
                  <p className="text-stone-700 font-body text-sm leading-relaxed mb-4">
                    Explore anything that arises. This takes much, much practice to notice our minds. If you’ve already
                    been practicing mindfulness, you’ll already have been practicing paying attention to the mind — and
                    its tricks. While we can simply <em>flip</em> what we think into a more supportive thought (which
                    <em> is</em> a great idea), it can also be very important to be aware of the programs our mind is running.
                  </p>
                  <textarea
                    value={entries.thoughts}
                    onChange={(e) => update('thoughts', e.target.value)}
                    placeholder="Notice the thoughts and the patterns behind them…"
                    rows={5}
                    className="w-full rounded-2xl border border-stone-200 bg-stone-50 focus:bg-white focus:border-green-400 focus:ring-1 focus:ring-green-300 outline-none p-4 font-body text-sm text-stone-700 resize-y transition-colors"
                    data-testid="reflection-thoughts-input"
                  />
                </div>

                {/* Private Notes */}
                <div data-testid="reflection-notes-block">
                  <h3 className="font-heading text-xl text-forest mb-2">My Private Notes</h3>
                  <p className="text-stone-700 font-body text-sm leading-relaxed mb-4">
                    A free space for anything else — reminders, wins, questions, or thoughts to bring to a session.
                  </p>
                  <textarea
                    value={entries.notes}
                    onChange={(e) => update('notes', e.target.value)}
                    placeholder="Write freely here…"
                    rows={5}
                    className="w-full rounded-2xl border border-stone-200 bg-stone-50 focus:bg-white focus:border-green-400 focus:ring-1 focus:ring-green-300 outline-none p-4 font-body text-sm text-stone-700 resize-y transition-colors"
                    data-testid="reflection-notes-input"
                  />
                </div>

                <div className="text-right">
                  <span
                    className={`text-xs font-body transition-opacity ${saved ? 'opacity-100 text-green-600' : 'opacity-0'}`}
                    data-testid="reflection-saved-indicator"
                  >
                    Saved privately ✓
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ReflectionJournal;
